import _ from "lodash";
import { Schema, SchemaField } from "types/schema";
import fp from "lodash/fp";
import { AggFuncs, IMAGE_COLUMN_MIN_WIDTH, Kinds, ValueTypes } from "constants/product-collection";
import { getViewDefFields } from "helpers/legacy-cart/line-items/view-definition-config-helper";
import { IViewDefinition, SECTIONS } from "types/view-definitions";
import { format } from "lib/globalize-util";
import { COLUMN_FIELDS, ColumnsKinds, MORE_MENU_COL_WIDTH, SizingViewComponents } from "constants/sizingView";
import { IMAGE_URL_FIELD_NAME } from "helpers/server-rollup";
import me from "models/me";
import {
    getFilterValueGetter,
    getValueFormatter,
    getValueGetter,
    ISchemaKey,
    PinnedValue,
} from "helpers/product-collection";
import type { ICurrency, IOrderCurrency } from "types/cart/shipment-review";
import { ICellRendererParams, ColDef } from "ag-grid-community";
import { ItemTotalType } from "containers/legacy-cart/helpers";

const DEFAULT_CELL_WIDTH = 132;
const rightAlignedParams = { headerClass: "right-aligned", cellClass: "right-aligned" };
const defaultAggFuncs = [_.toLower(AggFuncs.COUNT), _.toLower(AggFuncs.COUNT_WITH_PERCENT)];

/**
 * Converts a given product schema or order schema to collection schema.
 * The default object in the resultant list will always be "image_url".
 */
export const schemaToCollectionsSchema = (
    schema: Schema,
    itemView: IViewDefinition,
    options?: { currency?: IOrderCurrency; useBulk?: boolean; useShipmentColumn?: boolean }
): ColDef[] => {
    const viewDefFields = getViewDefFields(itemView);

    //grid should be sorted by view definition order
    const sortMap = {
        [COLUMN_FIELDS.LOCATION]: Infinity, // set locations after all VD fields
    };
    _.forEach(viewDefFields, (field, index) => {
        sortMap[field] = index;
    });

    const fieldsCols = _.chain(schema?.fields)
        .filter((field) => (me.isBuyer() ? field.display_to_buyers : field.display))
        .map((field) => getColDefFromSchemaField(field, viewDefFields))
        .concat(
            options?.useBulk
                ? constructSchemaFields(COLUMN_FIELDS.LOCATION, format("Location"), ValueTypes.STRING, {
                      gridColOptions: { rowGroup: true },
                  })
                : []
        )
        .sortBy((x) => sortMap[x.colId]) // sort by sorting in view definition
        .value();

    const columns = [
        getImageColumn(itemView),
        ...fieldsCols,
        constructSchemaFields("sizes", format("Sizes"), ValueTypes.STRING, {
            kind: Kinds.BRAND_ITEM_SIZES,
            gridColOptions: { cellClass: "sizes-cell" },
        }),
        constructSchemaFields("cost", format("Wholesale Price"), ValueTypes.RANGE, {
            kind: Kinds.PRICE_WITH_STRIKETHROUGH,
            formulas: [
                "flatten(values(fields('^wholesale_range'), 'range_value'))",
                "flatten(values(fields('^original_wholesale_range'), 'range_value'))",
            ],
            numberFormat: { currency: options?.currency?.wholesale },
            gridColOptions: { width: 150, ...rightAlignedParams },
        }),
        constructSchemaFields("retail", format("MSRP"), ValueTypes.RANGE, {
            kind: Kinds.FORMULA,
            formula: "flatten(values(fields('retail_range'), 'range_value'))",
            numberFormat: { currency: options?.currency?.retail },
            gridColOptions: rightAlignedParams,
        }),
        constructSchemaFields("total", format("Total Units"), ValueTypes.NUMBER, {
            kind: Kinds.FORMULA,
            formula: `totalBy(${ItemTotalType.Units})`,
            groupFormula: false,
            customAggregations: [AggFuncs.SUM, AggFuncs.SUM_WITH_PERCENT],
            gridColOptions: { initialAggFunc: "sum", ...rightAlignedParams },
        }),
        constructSchemaFields("total_retail", format("Total MSRP"), ValueTypes.NUMBER, {
            kind: Kinds.FORMULA,
            formula: `totalBy(${ItemTotalType.Retail})`,
            groupFormula: false,
            numberFormat: { currency: options?.currency?.retail },
            customAggregations: [AggFuncs.SUM, AggFuncs.SUM_WITH_PERCENT],
            gridColOptions: { initialAggFunc: "sum", ...rightAlignedParams },
        }),
        constructSchemaFields("total_cost", format("Total Wholesale"), ValueTypes.NUMBER, {
            kind: Kinds.FORMULA,
            formula: `totalBy(${ItemTotalType.Wholesale})`,
            groupFormula: false,
            numberFormat: { currency: options?.currency?.wholesale },
            customAggregations: [AggFuncs.SUM, AggFuncs.SUM_WITH_PERCENT],
            gridColOptions: { initialAggFunc: "sum", width: 150, ...rightAlignedParams },
        }),
        getMoreMenuCol(),
    ];

    if (options?.useShipmentColumn) {
        columns.push({
            colId: COLUMN_FIELDS.SHIPMENT,
            headerName: format("Delivery"),
            field: COLUMN_FIELDS.SHIPMENT,
            enableRowGroup: true,
            rowGroupActive: true,
            rowGroup: true,
            filter: "agSetColumnFilter",
        } as ColDef);
    }

    return columns;
};

// dates renderers is not support right now
const getColDefFromSchemaField = (field: SchemaField, viewDefFields: string[]): ColDef => ({
    colId: field.key,
    headerName: field.name,
    field: field.key,
    width: DEFAULT_CELL_WIDTH,
    cellRenderer: SchemaFieldsCellRenderers[field.key],
    valueGetter: getValueGetter({ field_name: field.key }),
    valueFormatter: getValueFormatter({ field_name: field.key }),
    filterValueGetter: getValueGetter({ field_name: field.key }),
    filter: "agSetColumnFilter",
    hide: !_.includes(viewDefFields, field.key),
    comparator: ColComparator?.[_.toUpper(field.details?.subtype)],
    allowedAggFuncs: getAllowedAggFuncs(),
});

interface SchemaFieldsOptions {
    kind?: Kinds | ColumnsKinds;
    numberFormat?: { currency?: ICurrency };
    gridColOptions?: Partial<ColDef>;
    customAggregations?: string[];
    formulas?: string[];
    formula?: string;
    groupFormula?: boolean;
}

/**
 * Construct a field in the schema for given attributes.
 * The properties under interest and the ones in parameters. Others essentially do not change.
 * See base template for other attributes.
 */
const constructSchemaFields = (
    field: string,
    title: string,
    valueType: ValueTypes,
    options: SchemaFieldsOptions = {}
): ColDef => {
    const { kind, numberFormat, gridColOptions, ...rest } = options;
    // TODO: remove schemaKey when getValueGetter, getValueFormatter, getFilterValueGetter will be refactored
    const schemaKey = {
        value_type: valueType,
        field_name: field,
        display_name: title,
        kind: kind ?? Kinds.DEFAULT_KIND,
        ...(numberFormat &&
            numberFormat.currency && {
                number_format: {
                    currency: {
                        currency_code: numberFormat.currency.code,
                        symbol: numberFormat.currency.symbol,
                        decimal_places: numberFormat.currency.decimalPlaces,
                    },
                },
            }),
        ...(_.size(gridColOptions) && { gridColOptions }),
        ...rest,
    } as ISchemaKey;
    return _.defaults({}, gridColOptions, {
        colId: field,
        headerName: title,
        field: field,
        cellRenderer: CellRenderers[kind],
        filter: ColFilter[valueType] ?? "agSetColumnFilter",
        floatingFilter: true,
        equals: (a, b) => _.isEqual(a, b),
        valueGetter: getValueGetter(schemaKey),
        valueFormatter: getValueFormatter(schemaKey),
        filterValueGetter: getFilterValueGetter(schemaKey),
        width: DEFAULT_CELL_WIDTH,
        type: CellTypes[valueType],
        comparator: ColComparator?.[valueType],
        allowedAggFuncs: getAllowedAggFuncs({
            customAggregations: options.customAggregations,
            type: valueType,
        }),
    });
};

export const NO_IMAGE_FIRST_COLUMN_WIDTH = 110;

/**
 * Sorting and filtering by image should be disabled.
 * Image column contains expand arrow and check box as well
 * so when image is disabled in view definition it should have less width.
 */
const getImageColumn = (itemView: IViewDefinition): ColDef => {
    const showImage = !!itemView?.sections?.[SECTIONS.SMALL_IMAGE];
    return constructSchemaFields(IMAGE_URL_FIELD_NAME, format("Image"), ValueTypes.STRING, {
        kind: ColumnsKinds.IMAGE_URL,
        gridColOptions: {
            filter: false,
            floatingFilter: false,
            sortable: false,
            suppressFiltersToolPanel: true,
            headerCheckboxSelection: true,
            headerCheckboxSelectionFilteredOnly: true,
            lockPosition: true,
            lockVisible: true,
            lockPinned: true,
            suppressMenu: true,
            resizable: true,
            suppressColumnsToolPanel: true,
            pinned: PinnedValue.LEFT,
            minWidth: IMAGE_COLUMN_MIN_WIDTH,
            // Image col contains also selection and arrow, that's why image is hidden but col should stay
            ...(!showImage && {
                width: NO_IMAGE_FIRST_COLUMN_WIDTH,
                maxWidth: NO_IMAGE_FIRST_COLUMN_WIDTH,
            }),
        },
    });
};

const getMoreMenuCol = () =>
    constructSchemaFields("more_menu", "", null, {
        kind: ColumnsKinds.MENU,
        gridColOptions: {
            cellClass: "more-menu",
            pinned: "right",
            width: MORE_MENU_COL_WIDTH,
            filter: false,
            resizable: false,
            suppressMenu: true,
            suppressColumnsToolPanel: true,
            suppressMovable: true,
            suppressCellFlash: true,
            cellRendererSelector: ({ node }: ICellRendererParams) => ({
                component: node.group
                    ? node.field === COLUMN_FIELDS.LOCATION
                        ? SizingViewComponents.LocationMenu
                        : null
                    : SizingViewComponents.ProductMenu,
            }),
        },
    });

const CellTypes = {
    [ValueTypes.NUMBER]: "numericColumn",
};

const ColFilter = {
    [ValueTypes.NUMBER]: "agNumberColumnFilter",
};

const SchemaFieldsCellRenderers = {
    name: SizingViewComponents.ProductNameRenderer,
};

const CellRenderers = {
    [ColumnsKinds.IMAGE_URL]: SizingViewComponents.ImageRenderer,
    [ColumnsKinds.PRICE_WITH_STRIKETHROUGH]: SizingViewComponents.PriceWithStrikethrough,
};

export const getAllowedAggFuncs = (options?: { customAggregations?: string[]; type?: ValueTypes }): string[] => {
    if (options?.customAggregations) {
        return [...defaultAggFuncs, ...fp.map(fp.toLower, options.customAggregations)];
    }
    if (options?.type === ValueTypes.NUMBER) {
        return [
            ...defaultAggFuncs,
            _.toLower(AggFuncs.SUM),
            _.toLower(AggFuncs.AVG),
            _.toLower(AggFuncs.SUM_WITH_PERCENT),
        ];
    }

    return defaultAggFuncs;
};

const numberComparator = (a: number, b: number) => a - b;

const rangeComparator = (a, b) => {
    // get actual values in case of strikethrough multidimensional array, ex. [[...prices], [...originalPrices]]
    const valueA = _.isArray(_.head(a)) ? _.head(a) : a;
    const valueB = _.isArray(_.head(b)) ? _.head(b) : b;

    return Math.min(...valueA) - Math.min(...valueB) || Math.max(...valueA) - Math.max(...valueB);
};

// STRING using default comparator
const ColComparator = {
    [ValueTypes.NUMBER]: numberComparator,
    [ValueTypes.RANGE]: rangeComparator,
};
